import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AttendancePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedCode, setSelectedCode] = useState('');
  const [projectDetails, setProjectDetails] = useState(null);
  
  // Hardcoded work codes
  const workCodes = [
    { id: 1, code: 'WC-2025-001' },
    { id: 2, code: 'WC-2025-002' },
    { id: 3, code: 'WC-2025-003' },
    { id: 4, code: 'WC-2025-004' },
    { id: 5, code: 'WC-2025-005' },
    { id: 6, code: 'WC-2025-006' },
    { id: 7, code: 'WC-2025-007' },
    { id: 8, code: 'WC-2025-008' },
    { id: 9, code: 'WC-2025-009' },
    { id: 10, code: 'WC-2025-010' }
  ];

  // Hardcoded project details for demo
  const projectDetailsData = {
    'WC-2025-001': { amount: '₹150,000', date: '2025-03-15', type: 'Training Program', participants: '50' },
    'WC-2025-002': { amount: '₹200,000', date: '2025-03-20', type: 'Infrastructure Development', participants: '30' },
    'WC-2025-003': { amount: '₹175,000', date: '2025-03-25', type: 'Skill Development', participants: '45' },
    'WC-2025-004': { amount: '₹185,000', date: '2025-04-10', type: 'Educational Workshop', participants: '60' },
    'WC-2025-005': { amount: '₹165,000', date: '2025-04-15', type: 'Health Camp', participants: '100' },
    'WC-2025-006': { amount: '₹155,000', date: '2025-04-20', type: 'Environmental Program', participants: '40' },
    'WC-2025-007': { amount: '₹190,000', date: '2025-05-05', type: 'Technical Training', participants: '35' },
    'WC-2025-008': { amount: '₹145,000', date: '2025-05-10', type: 'Agricultural Development', participants: '55' },
    'WC-2025-009': { amount: '₹160,000', date: '2025-05-15', type: 'Women Empowerment', participants: '70' },
    'WC-2025-010': { amount: '₹170,000', date: '2025-05-20', type: 'Youth Development', participants: '65' }
  };
  
  const [batches, setBatches] = useState([{
    id: 1,
    fromDate: '',
    toDate: '',
    maleParticipants: '',
    femaleParticipants: '',
    amountSpent: ''
  }]);

  // Handle work code selection
  const handleCodeSelect = (code) => {
    setSelectedCode(code);
    if (!code) {
      setProjectDetails(null);
      return;
    }

    // Set project details from hardcoded data
    setProjectDetails(projectDetailsData[code] || null);
  };

  // Handle batch field changes
  const handleBatchChange = (index, field, value) => {
    const updatedBatches = [...batches];
    updatedBatches[index][field] = value;
    setBatches(updatedBatches);
  };

  // Add new batch
  const addBatch = () => {
    setBatches([...batches, {
      id: batches.length + 1,
      fromDate: '',
      toDate: '',
      maleParticipants: '',
      femaleParticipants: '',
      amountSpent: ''
    }]);
  };

  // Remove batch
  const removeBatch = (index) => {
    if (batches.length > 1) {
      const updatedBatches = batches.filter((_, i) => i !== index);
      setBatches(updatedBatches);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCode) {
      alert('Please select a work code');
      return;
    }

    // Validate batch data
    const isValid = batches.every(batch => 
      batch.fromDate && batch.toDate && batch.maleParticipants && 
      batch.femaleParticipants && batch.amountSpent
    );

    if (!isValid) {
      alert('Please fill all batch details');
      return;
    }

    setLoading(true);
    try {
      const data = {
        workCode: selectedCode,
        batches: batches.map(batch => ({
          fromDate: batch.fromDate,
          toDate: batch.toDate,
          maleParticipants: parseInt(batch.maleParticipants),
          femaleParticipants: parseInt(batch.femaleParticipants),
          amountSpent: parseFloat(batch.amountSpent)
        }))
      };

      // Replace with actual API call when ready
      console.log('Submitting attendance data:', data);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      alert('Attendance submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting attendance:', error);
      alert('Failed to submit attendance');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-b from-blue-50 to-white min-h-screen py-8 px-4">
      <div className="bg-white shadow-lg rounded-xl p-8 max-w-6xl mx-auto border border-gray-100">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 border-b pb-6">
          <div>
            <h2 className="text-2xl font-bold text-blue-800 mb-2">Attendance Entry</h2>
          </div>
        </div>

        {loading && (
          <div className="text-center mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-blue-700 font-medium">Loading data, please wait...</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Work Code Selection */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">Work Code Selection</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <label htmlFor="workCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Work Code <span className="text-red-500">*</span>
                </label>
                <select
                  id="workCode"
                  value={selectedCode}
                  onChange={(e) => handleCodeSelect(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 shadow-sm py-2 pl-3 pr-10 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition appearance-none"
                  required
                >
                  <option value="">Select Work Code</option>
                  {workCodes.map(code => (
                    <option key={code.id} value={code.code}>
                      {code.code}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 pt-6">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Project Details (Read-only) */}
          {projectDetails && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">Project Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <input
                    type="text"
                    value={projectDetails.amount}
                    readOnly
                    className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 bg-gray-50"
                  />
                </div>
            
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <input
                    type="text"
                    value={projectDetails.type}
                    readOnly
                    className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Participants</label>
                  <input
                    type="text"
                    value={projectDetails.participants}
                    readOnly
                    className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 bg-gray-50"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Batch-wise Attendance */}
          {selectedCode && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">Batch-wise Attendance</h3>
             
              </div>

              {batches.map((batch, index) => (
                <div key={batch.id} className="mb-6 p-6 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium text-gray-700">Batch {index + 1}</h4>
                    {batches.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBatch(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={batch.fromDate}
                        onChange={(e) => handleBatchChange(index, 'fromDate', e.target.value)}
                        className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        To Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={batch.toDate}
                        onChange={(e) => handleBatchChange(index, 'toDate', e.target.value)}
                        className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Male Participants <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={batch.maleParticipants}
                        onChange={(e) => handleBatchChange(index, 'maleParticipants', e.target.value)}
                        className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Female Participants <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={batch.femaleParticipants}
                        onChange={(e) => handleBatchChange(index, 'femaleParticipants', e.target.value)}
                        className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount Spent <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={batch.amountSpent}
                        onChange={(e) => handleBatchChange(index, 'amountSpent', e.target.value)}
                        className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 text-gray-700 focus:border-blue-600 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Form Actions */}
          <div className="pt-6 border-t border-gray-200">
            <div className="flex flex-col-reverse sm:flex-row justify-end space-y-4 space-y-reverse sm:space-y-0 sm:space-x-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-5 py-2.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !selectedCode}
                className="px-5 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttendancePage;